import { useCallback, useMemo, useRef, useState } from 'react';
import { apiJson } from '../utils/api';

const PAGE_SIZE = 100;

// 채널별 메시지 목록 상태
// { items, hasOlder, hasNewer, generation }
//  - hasNewer: 메시지 링크/검색으로 과거 위치에 이동해 최신 메시지까지 아직 받지 않은 상태
//  - generation: 목록이 통째로 교체될 때(첫 로드·위치 이동·최신으로 이동) 증가 → 화면이 스크롤 위치를 다시 잡는 신호
const emptyChannel = { items: [], hasOlder: false, hasNewer: false, generation: 0, loaded: false };

const mergeById = (a, b) => {
    const seen = new Set(a.map(m => m.id));
    return [...a, ...b.filter(m => !seen.has(m.id))];
};

export function useChannelMessages() {
    const [channels, setChannels] = useState({});
    const [loading, setLoading] = useState({ initial: false, older: false, newer: false });
    // 불러오기 실패를 화면에 알리기 위한 상태. 예전에는 console 에만 남겨서
    // 사용자는 "왜 이전 메시지가 안 나오지" 하고 계속 기다리게 됐다.
    const [loadError, setLoadError] = useState({ initial: null, older: null, newer: null });
    const stateRef = useRef(channels);
    stateRef.current = channels;
    const busyRef = useRef(new Set());

    const patch = useCallback((chId, updater) => setChannels(prev => {
        const current = prev[chId] || emptyChannel;
        return { ...prev, [chId]: { ...current, ...updater(current) } };
    }), []);

    // 같은 종류의 요청이 겹치지 않도록 막는다 (스크롤 이벤트가 연속으로 발생해도 1회만 요청)
    const guarded = useCallback(async (key, flag, fn) => {
        if (busyRef.current.has(key)) return;
        busyRef.current.add(key);
        setLoading(l => ({ ...l, [flag]: true }));
        setLoadError(e => (e[flag] ? { ...e, [flag]: null } : e));
        try { await fn(); }
        catch (err) {
            console.error(`메시지 로드 실패 (${key})`, err);
            setLoadError(e => ({ ...e, [flag]: '메시지를 불러오지 못했습니다.' }));
        }
        finally {
            busyRef.current.delete(key);
            setLoading(l => ({ ...l, [flag]: false }));
        }
    }, []);

    const loadLatest = useCallback((chId) => guarded(`latest:${chId}`, 'initial', async () => {
        const items = await apiJson(`/api/channels/${chId}/messages?limit=${PAGE_SIZE}`);
        patch(chId, cur => ({ items, hasOlder: items.length >= PAGE_SIZE, hasNewer: false, loaded: true, generation: cur.generation + 1 }));
    }), [guarded, patch]);

    const ensureLoaded = useCallback((chId) => {
        if (chId && !stateRef.current[chId]?.loaded) loadLatest(chId);
    }, [loadLatest]);

    const loadOlder = useCallback((chId) => {
        const cur = stateRef.current[chId];
        if (!cur?.hasOlder || !cur.items.length) return;
        return guarded(`older:${chId}`, 'older', async () => {
            const items = await apiJson(`/api/channels/${chId}/messages?before=${cur.items[0].id}&limit=${PAGE_SIZE}`);
            patch(chId, c => ({ items: mergeById(items, c.items), hasOlder: items.length >= PAGE_SIZE }));
        });
    }, [guarded, patch]);

    const loadNewer = useCallback((chId) => {
        const cur = stateRef.current[chId];
        if (!cur?.hasNewer || !cur.items.length) return;
        return guarded(`newer:${chId}`, 'newer', async () => {
            const items = await apiJson(`/api/channels/${chId}/messages?after=${cur.items[cur.items.length - 1].id}&limit=${PAGE_SIZE}`);
            patch(chId, c => ({ items: mergeById(c.items, items), hasNewer: items.length >= PAGE_SIZE }));
        });
    }, [guarded, patch]);

    // 연결이 끊겼다 다시 붙었을 때: 끊긴 동안 놓친 메시지를 이어 받는다 (받은 새 메시지 목록 반환)
    const resync = useCallback(async (chId) => {
        const cur = stateRef.current[chId];
        if (!cur?.loaded || cur.hasNewer) return [];
        const last = cur.items[cur.items.length - 1];
        if (!last) { await loadLatest(chId); return []; }
        try {
            const items = await apiJson(`/api/channels/${chId}/messages?after=${last.id}&limit=${PAGE_SIZE}`);
            if (items.length) patch(chId, c => ({ items: mergeById(c.items, items), hasNewer: c.hasNewer || items.length >= PAGE_SIZE }));
            return items;
        } catch { return []; }
    }, [loadLatest, patch]);

    // 특정 메시지 주변(앞 50 / 뒤 50)으로 이동
    const jumpTo = useCallback((chId, msgId) => guarded(`jump:${chId}`, 'initial', async () => {
        const data = await apiJson(`/api/channels/${chId}/messages/${msgId}/context`);
        patch(chId, cur => ({ items: data.messages, hasOlder: data.hasOlder, hasNewer: data.hasNewer, loaded: true, generation: cur.generation + 1 }));
    }), [guarded, patch]);

    const updateMessage = useCallback((chId, msgId, fn) => setChannels(prev => {
        const cur = prev[chId];
        if (!cur) return prev;
        return { ...prev, [chId]: { ...cur, items: cur.items.map(m => (m.id === msgId ? fn(m) : m)) } };
    }), []);

    // WebSocket 이벤트 반영. 처리했으면 true
    const applyEvent = useCallback((data) => {
        switch (data.type) {
            case 'chat':
                setChannels(prev => {
                    const cur = prev[data.channelId];
                    // 아직 안 받은 채널이거나, 과거 위치를 보고 있어 중간이 비어 있으면 붙이지 않는다
                    if (!cur?.loaded || cur.hasNewer || cur.items.some(m => m.id === data.id)) return prev;
                    return { ...prev, [data.channelId]: { ...cur, items: [...cur.items, data] } };
                });
                return true;
            case 'reactionUpdate':
                updateMessage(data.channelId, data.messageId, m => ({ ...m, reactions: data.reactions }));
                return true;
            case 'messageDeleted':
                updateMessage(data.channelId, data.messageId, m => ({ ...m, is_deleted: 1, deleted_by: data.deleted_by }));
                return true;
            case 'messageRestored':
                updateMessage(data.channelId, data.message.id, () => data.message);
                return true;
            case 'messageEdited':
                updateMessage(data.channelId, data.messageId, m => ({ ...m, message: data.newMessage, is_edited: 1, edited_at: data.edited_at }));
                return true;
            case 'clearChannel':
                patch(data.channelId, cur => ({ items: [], hasOlder: false, hasNewer: false, generation: cur.generation + 1 }));
                return true;
            case 'channel_deleted':
                setChannels(prev => { const next = { ...prev }; delete next[data.channelId]; return next; });
                return true;
            case 'nickname_changed':
                setChannels(prev => Object.fromEntries(Object.entries(prev).map(([id, cur]) => [id, {
                    ...cur, items: cur.items.map(m => (m.userId === data.userId ? { ...m, username: data.newUsername } : m)),
                }])));
                return true;
            default:
                return false;
        }
    }, [patch, updateMessage]);

    return useMemo(() => ({
        channels, loading, loadError, ensureLoaded, loadLatest, loadOlder, loadNewer, jumpTo, applyEvent, resync,
        getChannel: (id) => channels[id] || emptyChannel,
    }), [channels, loading, loadError, ensureLoaded, loadLatest, loadOlder, loadNewer, jumpTo, applyEvent, resync]);
}
